// Shared office-hours normalization.
//
// The `office_hours` collection only contains rows for days the property
// actually entered — days with no hours (commonly Saturday/Sunday) are simply
// absent from the data. To render them as "Closed" everywhere hours appear
// (header, footer, contact-info cards), we pad the list out to a full week,
// injecting empty placeholder rows for any missing day. Each consumer's own
// `formatHoursValue` turns an empty open/close pair into "Closed".

export interface OfficeHourLike {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const WEEK: { key: string; label: string }[] = [
  { key: 'MON', label: 'Monday' },
  { key: 'TUE', label: 'Tuesday' },
  { key: 'WED', label: 'Wednesday' },
  { key: 'THU', label: 'Thursday' },
  { key: 'FRI', label: 'Friday' },
  { key: 'SAT', label: 'Saturday' },
  { key: 'SUN', label: 'Sunday' },
];

const WEEK_KEYS = new Set(WEEK.map((d) => d.key));

/** Returns the canonical MON–SUN key for a day string, or null if unrecognized. */
function dayKey(day: string | null | undefined): string | null {
  if (!day) return null;
  const key = day.trim().toUpperCase().slice(0, 3);
  return WEEK_KEYS.has(key) ? key : null;
}

/**
 * Pads office-hours rows out to a full Monday–Sunday week. Days missing from
 * `raw` get an empty placeholder row (rendered as "Closed" by each consumer).
 *
 * Returns a new array, leaving the original binding untouched. When `raw`
 * contains no recognizable day rows the input is returned as-is so empty-state
 * handling (e.g. the footer's "Office hours will be displayed here.") is
 * preserved rather than replaced with seven "Closed" rows.
 */
export function fillWeekHours<T extends OfficeHourLike>(raw: T[]): (T | OfficeHourLike)[] {
  const present = new Set<string>();
  for (const entry of raw) {
    const key = dayKey(entry.day);
    if (key) present.add(key);
  }

  if (present.size === 0) return [...raw];

  const filled: (T | OfficeHourLike)[] = [...raw];
  for (const { key, label } of WEEK) {
    if (!present.has(key)) {
      filled.push({ day: label, open_time: '', close_time: '' });
    }
  }
  return filled;
}
